import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { Suspense, type ReactNode } from 'react';

import { ErrorBoundary } from './ErrorBoundary';
import { LoadingFallback } from './LoadingFallback';
import { QueryErrorPlaceholder } from './QueryErrorPlaceholder';

interface QueryBoundaryProps {
  children: ReactNode;
  loadingFallback?: ReactNode;
  errorFallback?: (error: unknown, reset: () => void) => ReactNode;
  resetKeys?: readonly unknown[];
}

const defaultLoadingFallback = <LoadingFallback />;

export function QueryBoundary({
  children,
  loadingFallback = defaultLoadingFallback,
  errorFallback,
  resetKeys,
}: QueryBoundaryProps): ReactNode {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          resetKeys={resetKeys}
          fallbackRender={({ error, resetErrorBoundary }) =>
            errorFallback ? (
              errorFallback(error, resetErrorBoundary)
            ) : (
              <QueryErrorPlaceholder error={error} onRetry={resetErrorBoundary} />
            )
          }
        >
          <Suspense fallback={loadingFallback}>{children}</Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
